import {Grid, Theme} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {numericQuantity} from 'numeric-quantity';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {localized, localizedInterpolation} from '../../../../../i18n/i18n';
import {AppState} from '../../../../../state/store';
import {getUnitLengthNameByCulture, validUnitsIds} from '../../../../../utilities/helpers/culture-settings-helper';
import {requiredValidatorDynamicMsg} from '../../../../../utilities/platform-helpers/validator-helper';
import {
  frameDepth,
  frameLength,
  hasFascia,
  numberOfTableFrameFittings,
} from '../../../../pages/products/configuration-constants';
import {DropdownOption} from '../../../default-components/baisc-dropdown/basic-search-dropdown';
import {DropdownInput} from '../dynamic-components/dropdown-input';
import {RecommondetionTextRow} from '../dynamic-components/recommondetion-text-row';
import {ValueInput} from '../dynamic-components/value-input';
import {getConfigurationParamByName} from '../../../../../utilities/helpers/product-helper';
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    recommendedText: {
      margin: '0 0 0 35px',
    },
    gridItem: {
      height: '120px',
    },
    gridITexttem: {
      height: '80px',
    },
  }),
);

interface PropsFromParent {
  productWithMotor: boolean;
}
export const SargSelector: FC<PropsFromParent> = React.memo((props) => {
  const classes = useStyles();
  const selectedProduct = useSelector((store: AppState) => store.projectReducer.selectedProduct);
  const {getValues, setValue} = useFormContext();
  const {unitsId} = useSelector((store: AppState) => store.urlSettingsReducer.cultureUrlParameters);
  const [tableFrameOptions, setTableFrameOptions] = useState<DropdownOption[]>([
    {label: localized('ZeroPackages'), value: 0},
    {label: localized('OnePackages'), value: 1},
    {label: localized('TwoPackages'), value: 2},
    {label: localized('ThreePackages'), value: 3},
    {label: localized('FourPackages'), value: 4},
    {label: localized('FivePackages'), value: 5},
  ]);

  const validators = useMemo(() => {
    if (props.productWithMotor) {
      return {
        ...requiredValidatorDynamicMsg(localized('FieldIsRequired')),
        disabled: getValues(hasFascia) === 'false',
      };
    } else {
      return !(getValues(hasFascia) === 'true')
        ? undefined
        : {
            ...requiredValidatorDynamicMsg(localized('FieldIsRequired')),
          };
    }
  }, [getValues, props.productWithMotor]);

  const handlePackageOptions = useCallback(
    (value: number) => {
      let hasOption = tableFrameOptions?.some((option) => option['value'] === value);
      if (!hasOption) {
        setTableFrameOptions([
          ...tableFrameOptions,
          {
            label: localizedInterpolation('NPackages', {
              value: value,
            }),
            value: value,
          },
        ]);
      }
    },
    [tableFrameOptions],
  );

  const packagesCallback = useCallback(() => {
    let uniformlength = getValues(frameLength) ? getValues(frameLength).replace(',', '.') : '0';
    let uniformDepth = getValues(frameDepth) ? getValues(frameDepth).replace(',', '.') : '0';
    let length = Number(numericQuantity(uniformlength));
    let depth = Number(numericQuantity(uniformDepth));
    let packages = Math.ceil(((length + depth) * 2) / (unitsId === validUnitsIds.iu ? 39.37 : 1000));

    handlePackageOptions(packages);
    setValue(numberOfTableFrameFittings, packages);
  }, [getValues, handlePackageOptions, setValue, unitsId]);

  useEffect(() => {
    let defaultPackages = Number(getConfigurationParamByName(selectedProduct, numberOfTableFrameFittings));
    handlePackageOptions(isNaN(defaultPackages) ? 0 : defaultPackages);
  }, [handlePackageOptions, selectedProduct, tableFrameOptions]);

  return (
    <>
      <Grid container spacing={3} className={classes.gridItem}>
        <ValueInput
          formName={frameLength}
          textInputLabel={localized('TableFrameLength') + ' (C)'}
          hintLabel={localized('RecommendTabelTopFullLength')}
          tooltipLabel={localized('ToolTipProduct1Step2_2')}
          formOnBlur={packagesCallback}
        />
      </Grid>

      <Grid container spacing={3} className={classes.gridItem}>
        <ValueInput
          formName={frameDepth}
          textInputLabel={localized('TableFrameDepth') + ' (D)'}
          tooltipLabel={localizedInterpolation('ToolTipProduct1Step2_3', {
            value1: getUnitLengthNameByCulture(unitsId),
          })}
          formOnBlur={packagesCallback}
        />
      </Grid>

      <Grid container spacing={1} className={classes.gridITexttem}>
        <RecommondetionTextRow
          recommondetionString={localizedInterpolation('TableFrameMountingSuggestionX', {
            value1: unitsId === validUnitsIds.iu ? `39.3 ${localized('Inches')}` : localized('Meter'),
          })}
        />
      </Grid>

      <Grid container spacing={3} className={classes.gridItem}>
        <DropdownInput
          formName={numberOfTableFrameFittings}
          options={tableFrameOptions}
          label={localized('NumberOfTableFrameFittings')}
          hintLabel={localized('ContainsFourFittings')}
          tooltipLabel={localized('ToolTipProduct1Step2_4')}
          validators={validators}
        />
      </Grid>
    </>
  );
});
